import { useCallback, useEffect, useState } from 'react';
import LangEnum from '../../lang/LangEnum';
import snackbarStore from '../../stores/Snackbar.store';
import { useTranslation } from '../translation/TranslationContext';
import { brandSplitSettingsRepository } from './brandSplitSettings.repository';
import {
  BrandSplitSettings,
  BrandSplitSettingsDTO,
  BrandSplitType,
} from './brandSplitSettings.types';

const useBrandSplitSettings = () => {
  const { translate } = useTranslation();

  const [splitSettings, setSplitSettings] = useState<BrandSplitSettings[]>([]);
  const [selectedSplitSettings, setSelectedSplitSettings] = useState<BrandSplitSettings[]>([]);

  const getLabel = useCallback(
    (brandSplit: BrandSplitSettingsDTO): string => {
      if (brandSplit.type === BrandSplitType.AVAILABILITY) {
        return translate(LangEnum.GLOBAL_AVAILABILITY);
      }

      if (brandSplit.type === BrandSplitType.DELIVERY_WINDOW) {
        return translate(LangEnum.GLOBAL_DELIVERY_WINDOW);
      }

      return brandSplit.label;
    },
    [translate],
  );

  const fetchSplitSettings = useCallback(async () => {
    const response = await brandSplitSettingsRepository.list();

    if (response.ok && response.data) {
      setSplitSettings(
        response.data.map((brandSplit) => ({
          ...brandSplit,
          label: getLabel(brandSplit),
          key: `${brandSplit.label}:${brandSplit.type}`,
        })),
      );
    }
  }, [getLabel]);

  const fetchSelectedSplitSettings = useCallback(async () => {
    const response = await brandSplitSettingsRepository.getBrandConfig();

    if (response.ok && response.data) {
      setSelectedSplitSettings(
        response.data.map((brandSplit) => ({
          ...brandSplit,
          key: `${brandSplit.label}:${brandSplit.type}`,
        })),
      );
    }
  }, []);

  const updateSplitSettings = useCallback(
    async (newSplitSettings: BrandSplitSettings[]) => {
      const response = await brandSplitSettingsRepository.updateOrCreate(newSplitSettings);

      if (!response.ok && response.data) {
        snackbarStore.handleMessage({
          type: 'error',
          message: (response.data as any).message
            ? (response.data as any).message
            : translate(LangEnum.GLOBAL_ERROR_MESSAGE),
        });
        return;
      }

      setSelectedSplitSettings(newSplitSettings);
    },
    [translate],
  );

  useEffect(() => {
    fetchSelectedSplitSettings();
  }, [fetchSelectedSplitSettings]);

  const orderSeparation = !!selectedSplitSettings.find(
    (split) =>
      split.type === BrandSplitType.AVAILABILITY || split.type === BrandSplitType.DELIVERY_WINDOW,
  );

  return {
    fetchSplitSettings,
    updateSplitSettings,
    splitSettings,
    selectedSplitSettings,
    orderSeparation,
  };
};

export default useBrandSplitSettings;
